import { JobType, Shift, ShiftStatus, ShiftTimeOfDay, ShiftPattern } from '../types';
import { format, parseISO, addHours, isBefore, isAfter, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { ko } from 'date-fns/locale';

// 날짜 포맷팅 유틸리티
export function formatShiftDate(dateString: string): string {
  try {
    // date-fns 사용 버전으로 통일
    return format(parseISO(dateString), 'yyyy년 M월 d일 (E)', { locale: ko });
  } catch (error) {
    console.error('날짜 포맷팅 오류:', error);
    return dateString || '-';
  }
}

// 시간 포맷팅 유틸리티 - 두 구현을 통합
export function formatShiftTime(startTime: string, endTime?: string): string {
  // 단일 시간 포맷팅 (첫 번째 구현)
  if (!endTime) {
    try {
      return format(parseISO(startTime), 'a h:mm', { locale: ko });
    } catch (error) {
      console.error('시간 포맷팅 오류:', error);
      return startTime || '-';
    }
  } 
  // 범위 시간 포맷팅 (두 번째 구현)
  else {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const formatTime = (date: Date) => {
        return date.toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      };
      
      return `${formatTime(start)} - ${formatTime(end)}`;
    } catch (error) {
      console.error('시간 범위 포맷팅 오류:', error);
      return `${startTime || '-'} - ${endTime || '-'}`;
    }
  }
}

// 시프트 시간 범위 포맷팅 수정 (옵셔널 endTime 사용)
export function formatShiftTimeRange(startTime: string, endTime: string): string {
  try {
    const formattedStart = formatShiftTime(startTime);
    const formattedEnd = formatShiftTime(endTime);
    return `${formattedStart} - ${formattedEnd}`;
  } catch (error) {
    console.error('시간 범위 포맷팅 오류:', error);
    return `${startTime} - ${endTime}`;
  }
}

// 시프트 기간에 따른 총 급여 계산
export function calculateTotalPay(startTime: string, endTime: string, hourlyRate: number): number {
  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    
    // 시간 차이 계산 (밀리초)
    const diffInMilliseconds = end.getTime() - start.getTime();
    
    // 시간 차이 계산 (시간)
    const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
    
    // 총 급여 계산 및 반올림 (소수점 2자리)
    return Math.round(diffInHours * hourlyRate * 100) / 100;
  } catch (error) {
    console.error('총 급여 계산 오류:', error);
    return 0;
  }
}

// 시간대 판별 함수
export function determineTimeOfDay(startTime: string, endTime: string): ShiftTimeOfDay {
  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const startHour = start.getHours();

    // AM: 6:00 ~ 13:59, PM: 14:00 ~ 21:59, NOC: 22:00 ~ 5:59
    if (startHour >= 6 && startHour < 14) {
      return ShiftTimeOfDay.AM;
    } else if (startHour >= 14 && startHour < 22) {
      return ShiftTimeOfDay.PM;
    } else {
      return ShiftTimeOfDay.NOC;
    }
  } catch (error) {
    console.error('시간대 판별 오류:', error);
    return ShiftTimeOfDay.CUSTOM;
  }
}

// 시프트 상태에 따른 색상 반환
export const getShiftStatusColor = (status: string): string => {
  switch (status) {
    case ShiftStatus.OPEN:
      return '#4caf50'; // green
    case ShiftStatus.BOOKED:
      return '#2196f3'; // blue
    case ShiftStatus.COMPLETED:
      return '#607d8b'; // blue grey
    case ShiftStatus.CANCELLED:
      return '#f44336'; // red
    default:
      return '#9e9e9e'; // grey
  }
};

// 간호사 직군 한글 이름 - 두 구현을 통합
export function getJobTypeLabel(jobType: string | JobType): string {
  // 문자열이든 enum이든 처리 가능하도록
  const jobTypeStr = typeof jobType === 'string' ? jobType : String(jobType);
  
  switch (jobTypeStr) {
    // 첫 번째 구현의 케이스
    case JobType.CNA:
    case 'CNA':
      return '간호조무사';
    case JobType.LPN:
    case 'LPN':
      return '실무 간호사';
    case JobType.RN:
    case 'RN':
      return '정규 간호사';
    case JobType.NP:
    case 'NP':
      return '전문 간호사';
      
    // 두 번째 구현의 케이스
    case 'APN':
      return '전문 간호사';
    case 'HCN':
      return '가정 간호사';
    case 'OR':
      return '수술실 간호사';
    case 'ICU':
      return '중환자실 간호사';
    case 'ER':
      return '응급실 간호사';
    case JobType.Other:
    case 'Other':
      return '기타';
    default:
      return String(jobType);
  }
}

// 시간대 한글 이름
export function getTimeOfDayLabel(timeOfDay: ShiftTimeOfDay): string {
  switch (timeOfDay) {
    case ShiftTimeOfDay.AM:
      return '오전';
    case ShiftTimeOfDay.PM:
      return '오후';
    case ShiftTimeOfDay.NOC:
      return '야간';
    case ShiftTimeOfDay.CUSTOM:
      return '사용자 지정';
    default:
      return timeOfDay;
  }
}

// 시프트를 캘린더 이벤트로 변환
export function shiftToCalendarEvent(shift: Shift) {
  const statusColors = getShiftStatusColor(shift.status);
  
  return {
    id: shift.id,
    title: `${getJobTypeLabel(shift.jobType)} - ${formatShiftTime(shift.startTime)}`,
    start: shift.startTime,
    end: shift.endTime,
    allDay: false,
    backgroundColor: statusColors,
    borderColor: statusColors,
    textColor: statusColors,
    extendedProps: {
      shift: {
        ...shift,
        formattedStartTime: formatShiftTime(shift.startTime),
        formattedEndTime: formatShiftTime(shift.endTime),
      },
    },
  };
}

// 시프트 필터링 함수
export function filterShifts(shifts: Shift[], filters: { jobTypes?: JobType[], timeOfDay?: ShiftTimeOfDay[], searchQuery?: string }) {
  return shifts.filter((shift) => {
    // 직무 유형 필터링
    if (filters.jobTypes && filters.jobTypes.length > 0) {
      if (!filters.jobTypes.includes(shift.jobType)) {
        return false;
      }
    }
    
    // 시간대 필터링
    if (filters.timeOfDay && filters.timeOfDay.length > 0) {
      // timeOfDay가 정의되어 있지 않으면 시작/종료 시간으로부터 결정
      const shiftTimeOfDay = shift.timeType || determineTimeOfDay(shift.startTime, shift.endTime);
      if (!filters.timeOfDay.includes(shiftTimeOfDay)) {
        return false;
      }
    }
    
    // 검색어 필터링
    if (filters.searchQuery && filters.searchQuery.trim() !== '') {
      const searchLower = filters.searchQuery.toLowerCase().trim();
      const jobTypeLabel = getJobTypeLabel(shift.jobType).toLowerCase();
      const departmentName = shift.departmentName?.toLowerCase() || '';
      // 간호사 이름 속성이 있는 경우에만 사용 (타입에 없을 수 있음)
      const nurseNameLower = (shift as any).nurseName?.toLowerCase() || '';
      const shiftIdLower = shift.id.toLowerCase();
      const shiftPatternLower = shift.pattern ? String(shift.pattern).toLowerCase() : '';
      
      // 시프트 ID, 직무 유형, 부서명, 간호사 이름, 패턴 중 하나라도 검색어를 포함하는지 확인
      const matchesSearch = 
        shiftIdLower.includes(searchLower) ||
        jobTypeLabel.includes(searchLower) ||
        departmentName.includes(searchLower) ||
        nurseNameLower.includes(searchLower) ||
        shiftPatternLower.includes(searchLower);
        
      if (!matchesSearch) {
        return false;
      }
    }
    
    return true;
  });
}

// 더미 시프트 생성을 위한 옵션
export interface DummyShiftOptions {
  hospitalId?: string;
  departmentId?: string;
  startDate?: Date;
  endDate?: Date;
}

// 오버로드: 갯수 기반 더미 시프트 생성
export function generateDummyShifts(count: number, options?: DummyShiftOptions): Shift[];
// 오버로드: 날짜 범위 기반 더미 시프트 생성
export function generateDummyShifts(
  startDate: Date,
  endDate: Date,
  hospitalId?: string,
  departmentId?: string
): Shift[];

// 구현
export function generateDummyShifts(
  countOrStartDate: number | Date,
  optionsOrEndDate?: DummyShiftOptions | Date,
  hospitalId: string = 'test-hospital-id',
  departmentId: string = 'dept-1'
): Shift[] {
  // count & options 오버로드 처리
  if (typeof countOrStartDate === 'number') {
    const count = countOrStartDate;
    const options = optionsOrEndDate as DummyShiftOptions || {};
    
    const startDate = options.startDate || new Date();
    const endDate = options.endDate || new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000); // +30일
    const hospId = options.hospitalId || hospitalId;
    const deptId = options.departmentId || departmentId;
    
    return generateDummyShiftsByDateRange(startDate, endDate, hospId, deptId, count);
  }
  
  // startDate & endDate 오버로드 처리
  const startDate = countOrStartDate as Date;
  const endDate = optionsOrEndDate as Date;
  
  return generateDummyShiftsByDateRange(startDate, endDate, hospitalId, departmentId);
}

// 내부 구현: 날짜 범위 기반 더미 시프트 생성
function generateDummyShiftsByDateRange(
  startDate: Date,
  endDate: Date,
  hospitalId: string = 'test-hospital-id',
  departmentId: string = 'dept-1',
  targetCount?: number
): Shift[] {
  const shifts: Shift[] = [];
  const departments = [
    { id: 'dept-1', name: '내과' },
    { id: 'dept-2', name: '외과' },
    { id: 'dept-3', name: '소아과' },
    { id: 'dept-4', name: '응급의학과' },
  ];
  
  // 모든 JobType 배열
  const jobTypes = Object.values(JobType);
  
  // 모든 ShiftStatus 배열
  const statuses = Object.values(ShiftStatus);
  
  // 시작 날짜부터 종료 날짜까지 반복
  let currentDate = startOfMonth(startDate);
  const lastDate = endOfMonth(endDate);
  
  while ((isBefore(currentDate, lastDate) || currentDate.getDate() === lastDate.getDate()) && 
         (!targetCount || shifts.length < targetCount)) {
    // 각 날짜에 대해 랜덤 개수(0-3)의 시프트 생성
    const shiftsPerDay = Math.floor(Math.random() * 4);
    
    for (let i = 0; i < shiftsPerDay && (!targetCount || shifts.length < targetCount); i++) {
      // 랜덤 직무 유형
      const randomJobType = jobTypes[Math.floor(Math.random() * jobTypes.length)];
      
      // 랜덤 부서
      const randomDepartment = departments[Math.floor(Math.random() * departments.length)];
      
      // 랜덤 시간대 (AM/PM/NOC)
      const timeOfDayRandom = Math.floor(Math.random() * 3);
      let startHour: number;
      
      if (timeOfDayRandom === 0) { // AM
        startHour = 7 + Math.floor(Math.random() * 5); // 7-11 AM
      } else if (timeOfDayRandom === 1) { // PM
        startHour = 15 + Math.floor(Math.random() * 5); // 3-7 PM
      } else { // NOC
        startHour = 23; // 11 PM
      }
      
      // 시작 및 종료 시간
      const shiftStart = new Date(currentDate);
      shiftStart.setHours(startHour, 0, 0);
      
      const shiftEnd = addHours(shiftStart, 8); // 8시간 시프트
      
      // 시급
      const hourlyRate = 20 + Math.floor(Math.random() * 30); // $20-$50
      
      // 상태 (70% open, 10% booked, 10% completed, 10% cancelled)
      const statusRandom = Math.random();
      let status: ShiftStatus;
      
      if (statusRandom < 0.7) {
        status = ShiftStatus.OPEN;
      } else if (statusRandom < 0.8) {
        status = ShiftStatus.BOOKED;
      } else if (statusRandom < 0.9) {
        status = ShiftStatus.COMPLETED;
      } else {
        status = ShiftStatus.CANCELLED;
      }
      
      // 고유 ID 생성
      const id = `shift-${currentDate.toISOString()}-${i}`;
      
      // 시프트 객체 생성
      const shift: Shift = {
        id,
        hospitalId,
        departmentId: randomDepartment.id,
        departmentName: randomDepartment.name,
        jobType: randomJobType,
        startDate: format(currentDate, 'yyyy-MM-dd'),
        endDate: format(currentDate, 'yyyy-MM-dd'),
        pattern: ShiftPattern.ALL_DAYS,
        timeType: timeOfDayRandom === 0 ? ShiftTimeOfDay.AM : 
                  timeOfDayRandom === 1 ? ShiftTimeOfDay.PM : ShiftTimeOfDay.NOC,
        shiftDate: format(currentDate, 'yyyy-MM-dd'),
        startTime: shiftStart.toISOString(),
        endTime: shiftEnd.toISOString(),
        hourlyRate,
        totalPay: hourlyRate * 8,
        status,
        experienceYears: Math.floor(Math.random() * 5),
        educationLevel: '무관',
        licenseRequired: Math.random() > 0.3,
        jobDescription: `${randomDepartment.name} ${getJobTypeLabel(randomJobType)} 근무`,
        additionalNotes: Math.random() > 0.7 ? '특이사항 없음' : undefined,
        createdById: 'admin-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        formattedStartTime: formatShiftTime(shiftStart.toISOString()),
        formattedEndTime: formatShiftTime(shiftEnd.toISOString()),
        timeOfDay: determineTimeOfDay(shiftStart.toISOString(), shiftEnd.toISOString()),
      };
      
      shifts.push(shift);
    }
    
    // 다음 날짜로 이동
    currentDate = addDays(currentDate, 1);
  }
  
  // 특정 개수가 요청된 경우 정확한 개수를 반환
  if (targetCount && shifts.length < targetCount) {
    // 부족한 개수만큼 첫 번째 항목을 복제해서 채우기
    const template = shifts[0] || {
      id: `shift-template`,
      hospitalId,
      departmentId: 'dept-1',
      departmentName: '내과',
      jobType: JobType.RN,
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      pattern: ShiftPattern.ALL_DAYS,
      timeType: ShiftTimeOfDay.AM,
      shiftDate: format(new Date(), 'yyyy-MM-dd'),
      startTime: new Date().toISOString(),
      endTime: new Date(new Date().getTime() + 8 * 60 * 60 * 1000).toISOString(),
      hourlyRate: 25,
      totalPay: 200,
      status: ShiftStatus.OPEN,
      experienceYears: 1,
      educationLevel: '무관',
      licenseRequired: true,
      jobDescription: '간호사 근무',
      createdById: 'admin-user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      formattedStartTime: '09:00',
      formattedEndTime: '17:00',
      timeOfDay: ShiftTimeOfDay.AM,
    };
    
    for (let i = shifts.length; i < targetCount; i++) {
      const timeOffset = i * 24 * 60 * 60 * 1000; // 1일씩 증가
      const shiftDate = new Date(new Date().getTime() + timeOffset);
      
      shifts.push({
        ...template,
        id: `shift-${i}-${shiftDate.toISOString()}`,
        startDate: format(shiftDate, 'yyyy-MM-dd'),
        endDate: format(shiftDate, 'yyyy-MM-dd'),
        shiftDate: format(shiftDate, 'yyyy-MM-dd'),
        startTime: new Date(shiftDate.setHours(9, 0, 0)).toISOString(),
        endTime: new Date(shiftDate.setHours(17, 0, 0)).toISOString(),
      });
    }
  }
  
  return shifts;
}

// 시프트 상태에 따른 라벨 반환
export const getShiftStatusLabel = (status: string): string => {
  switch (status) {
    case ShiftStatus.OPEN:
      return '모집 중';
    case ShiftStatus.BOOKED:
      return '예약됨';
    case ShiftStatus.COMPLETED:
      return '완료됨';
    case ShiftStatus.CANCELLED:
      return '취소됨';
    default:
      return status;
  }
}; 